import { useCallback, useState } from 'react';
import { BrowserRouter } from 'react-router';
import { env } from '@/env';
import { App } from '@/App';
import { SignInLanding } from '@/components/SignInLanding/SignInLanding';
import { resolveSession } from '@/lib/bpSession';
import { deferEnrolment, readDeferred } from '@/lib/enrolmentDeferral';
import {
  enrollPasskey,
  platformAuthenticatorAvailable,
  signInWithPasskey,
} from '@/lib/passkeyDriver';
import { canHoldSession, webAuthnSupported } from '@bp-files/client';
import type { Me } from '@/lib/api';

/**
 * Gate first, app second (ADR-0034).
 *
 * The app used to sign in inside `main.tsx`'s `boot()`, before `createRoot` —
 * so the redirect round trip was a blank frame and there was no anonymous state
 * for a landing page to be. It mounts anonymous now, and this is the seam.
 *
 * **`<BrowserRouter>` lives here rather than in `main.tsx`, and that is the
 * load-bearing half of the change.** `lib/msal.ts` restores a CON-15 deep link
 * with `history.replaceState` once it has consumed the authorization response,
 * and `replaceState` fires **no `popstate`**. A router mounted above the gate
 * would have read `/` on the way in and would never hear the correction: every
 * space link an agency emailed would open Files instead, silently. Mounting the
 * router only once verification has resolved means the URL it first reads is
 * already the right one. `Bootstrap.test.tsx` guards it.
 *
 * The failure mode is worth stating because of its shape: it breaks only deep
 * spaces, which is the one case deep links exist for, and it passes every test
 * that starts at `/`.
 */

/**
 * _Amended twice._ **The mock/real switch moved to `@bp-files/client`** when the
 * operator console became a second app that needed exactly it (M9), and the pair
 * of functions it produces moved again — down to `lib/bpSession.ts` — when
 * `GET /me` became the thing that decides whether anybody is signed in
 * (ADR-0050). What is left here is composition: this file names the seams and
 * threads no auth logic of its own.
 *
 * The invariant is unchanged and now lives two files down: **a mocked build
 * never downloads MSAL**, because `authDriver` reaches it through a dynamic
 * `import()` and the package's barrel deliberately does not re-export it. So
 * `pnpm -r test` and `pnpm dev` still need no Entra configuration at all
 * (Article 5, ADR-0027). It is stronger than it was: an anonymous visitor who
 * never clicks the button now downloads no auth library at all, because the
 * check that used to reach MSAL on every load asks the API instead.
 */

/**
 * Whether this build can complete a passkey sign-in, as opposed to merely
 * starting one.
 *
 * **An assertion mints a session, and a session is a cookie** (ADR-0045) — so in
 * a two-origin deployment the ceremony verifies, the browser discards the
 * `Set-Cookie`, and the gate hands over to an app with no session. The user
 * sees a passkey prompt, satisfies it, and lands on the refused-session screen:
 * a control that appears to work, does work, and strands them anyway.
 *
 * So the control is not offered at all until `/api` is same-origin. This is the
 * same condition `openBpSession` already refuses on and for the same reason,
 * which is why it reads the same value rather than a flag of its own.
 *
 * **It used to be false in dev, and that is most of why the landing page has
 * been rewritten four times** (ADR-0051). `.env.example` named `localhost:7071`
 * while `vite.config.ts` was already proxying `/api` to it, so every local run
 * withheld the passkey control, ignored the hint, and took the passcode leg. The
 * decision tree this value gates had never executed on a developer's machine.
 * `.env.example` points at `/api` now; this guard is unchanged.
 *
 * **Enrolment is deliberately not gated.** That ceremony is authenticated by the
 * bootstrap credential and stores a credential — no session involved — so it
 * works cross-origin, and somebody can register a passkey today that becomes
 * useful the day the origin unifies.
 */
const canSignInWithPasskey = canHoldSession(env.VITE_API_BASE_URL, env.VITE_SIMULATED_AUTH);

/**
 * **The passcode is gated on the same value, and for the identical reason.**
 *
 * Everything the paragraph above says about a passkey ceremony on a two-origin
 * build is true of the passcode: the API mints a session, the browser discards
 * the `Set-Cookie`, and the person lands back on this page having read their
 * mail, typed eight digits, and achieved nothing. One value gates both because
 * it is one fact — whether a session can exist here at all.
 *
 * With both withheld a two-origin build offers no sign-in whatever, which is
 * deliberate and is the honest rendering: `offerFor` answers `'unavailable'`
 * and the control says so.
 *
 * **`VITE_SIMULATED_AUTH` is the other way to answer yes, and deployed dev is
 * now that build** (ADR-0059). It is the "properly mocked development identity"
 * this paragraph used to name as the roadmap item, and deliberately not "another
 * credential that half works": the credential is the same session row and the
 * same secret, carried in a header because the cookie cannot travel. Both
 * controls light up together for the same reason they went dark together — it
 * is still one fact, whether a session can exist here at all.
 */
const canSignInWithPasscode = canHoldSession(env.VITE_API_BASE_URL, env.VITE_SIMULATED_AUTH);

export interface BootstrapProps {
  /**
   * Injected by `Bootstrap.test.tsx` only. Defaulted rather than required, on
   * `ExportButton`'s precedent: the one production call site passes nothing and
   * reads as the thing it is.
   */
  readonly resolve?: () => Promise<Me | undefined>;
}

export function Bootstrap({ resolve = resolveSession }: BootstrapProps = {}) {
  /*
    `undefined` means "still at the gate". Wrapped in an object rather than
    holding `Me | undefined` directly, because the prefetch legitimately
    resolves to `undefined` — a bare `Me | undefined` could not tell "the app is
    ready and /me failed" from "the gate has not finished".
  */
  const [ready, setReady] = useState<{ me: Me | undefined } | undefined>(undefined);

  const onReady = useCallback((me: Me | undefined) => {
    setReady({ me });
  }, []);

  if (ready === undefined) {
    return (
      <SignInLanding
        /*
          **The check and the prefetch are one call now.** `resolveSession`
          answers "is anybody signed in" with `GET /me` itself, so the `Me` it
          returns is both the answer and the seed — there is no second request
          for the click to wait on, and no way for the two to disagree. It also
          still owns the credential hand-over on the passcode leg: the exchange
          resolves before `/me` is asked, so `/me` is the first request the
          cookie answers rather than the last one the bearer does (ADR-0049).
        */
        resolve={resolve}
        canPasscode={canSignInWithPasscode}
        onReady={onReady}
        /*
          Read once, here, rather than by the page: this is the app's storage and
          the landing is a component that should be renderable from a test with a
          boolean.

          **There used to be two more props beside this one** — `passkeyUsedHere`
          and `forgetPasskeyHint`, reading and clearing a `localStorage` record of
          whether a passkey had been used on this browser. ADR-0083 deleted the
          record: WebAuthn will not tell anybody whether a credential exists, iOS
          would not keep the guess we made instead, and the page no longer needs
          one. `enrolmentDeferred` survives because it records a *choice somebody
          made* rather than a fact about a device, which is a thing storage can
          actually hold.
        */
        enrolmentDeferred={readDeferred()}
        onDeferEnrolment={deferEnrolment}
        /*
          Enrolment and the capability probe are unconditional; only the
          assertion is gated (see `canSignInWithPasskey` above). `offerFor` will
          not offer enrolment on a build that cannot then assert with it — that
          is its own rule and not this file's.
        */
        enroll={enrollPasskey}
        platformAvailable={platformAuthenticatorAvailable}
        webAuthnSupported={webAuthnSupported()}
        {...(canSignInWithPasskey ? { signInWithPasskey } : {})}
      />
    );
  }

  return (
    <BrowserRouter>
      {/*
        Path-based routing, not hash: CON-15 space links get pasted into email,
        and a clean address is what an agency will trust. The cost is that
        whatever serves this bundle must rewrite unknown paths to index.html —
        see ADR-0007.
      */}
      <App initialMe={ready.me} />
    </BrowserRouter>
  );
}
